/**
 * Auth Reset Controller (EPIC-001 / 1.4)
 *
 * Reads the reset token from the URL query string, validates the new
 * password client-side (mirrors PasswordPolicy server-side: ≥12 chars,
 * ≥1 uppercase, ≥1 digit) and submits to POST /auth/reset.
 *
 *   200/204 ⇒ redirect /connexion.html?reset=success (toast picked up by login)
 *   401     ⇒ show invalid-token block + lien « Demander un nouveau lien »
 *   422     ⇒ surface server-side violations under the form
 *   other   ⇒ generic retry message
 *
 * Validation rules are reflected in real time in the rules list (data-rule
 * targets) and the confirm-mismatch hint, both via DOM toggles only — no
 * HTML inline (CLAUDE.md rule 8).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "../services/api.js";
import {
  validatePassword,
  formatViolations,
  checkRules,
} from "../utils/password_policy.js";

export default class AuthResetController extends BaseController {
  static targets = [
    "form",
    "password",
    "passwordConfirm",
    "submit",
    "error",
    "rule",
    "mismatch",
    "invalidTokenBlock",
  ];

  connect() {
    this.clearError();
    const params = new URLSearchParams(window.location.search);
    this.token = params.get("token") || "";

    if (!this.token) {
      this.showInvalidTokenBlock();
      return;
    }

    this.updateRules();
    if (this.hasPasswordTarget) {
      queueMicrotask(() => this.passwordTarget.focus());
    }
  }

  async submit(event) {
    event.preventDefault();

    const password = this.passwordTarget.value;
    const confirm = this.passwordConfirmTarget.value;

    this.clearError();

    const violations = validatePassword(password);
    if (violations.length > 0) {
      this.showFormError(formatViolations(violations));
      this.passwordTarget.focus();
      return;
    }

    if (password !== confirm) {
      this.showFormError("Les deux mots de passe ne correspondent pas.");
      this.passwordConfirmTarget.focus();
      return;
    }

    this.setBusy(true);

    try {
      await api.post("/auth/reset", { token: this.token, password });
      window.location.assign("/connexion.html?reset=success");
    } catch (err) {
      this.handleResetError(err);
    } finally {
      this.setBusy(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Live rule + mismatch indicators
  // ---------------------------------------------------------------------------

  updateRules() {
    if (!this.hasPasswordTarget || !this.hasRuleTarget) return;
    const checks = checkRules(this.passwordTarget.value);

    for (const li of this.ruleTargets) {
      const rule = li.dataset.rule;
      const ok = checks[rule] === true;
      li.classList.toggle("is-ok", ok);
      const mark = li.querySelector(".nm-check");
      if (mark) mark.textContent = ok ? "✓" : "○";
    }
  }

  updateMismatch() {
    if (!this.hasMismatchTarget) return;
    const confirm = this.passwordConfirmTarget.value;
    if (confirm.length === 0) {
      this.mismatchTarget.hidden = true;
      return;
    }
    this.mismatchTarget.hidden = (this.passwordTarget.value === confirm);
  }

  // ---------------------------------------------------------------------------
  // Error handling
  // ---------------------------------------------------------------------------

  handleResetError(err) {
    const status = err instanceof APIError ? err.status : 0;
    if (status === 401) {
      this.showInvalidTokenBlock();
      return;
    }
    if (status === 422) {
      const serverViolations = err?.data?.error?.details?.violations;
      if (Array.isArray(serverViolations) && serverViolations.length > 0) {
        this.showFormError(formatViolations(serverViolations));
        return;
      }
      this.showFormError("Mot de passe non conforme.");
      return;
    }
    if (status === 0 || status === 408 || status >= 500) {
      this.showFormError("Réseau injoignable. Réessayez.");
      return;
    }
    this.showFormError("Une erreur est survenue. Réessayez.");
  }

  showFormError(message) {
    if (!this.hasErrorTarget) return;
    const fragment = this.cloneTemplate("tpl-form-error");
    this.fillSlots(fragment, { message });
    this.errorTarget.replaceChildren(fragment);
    this.errorTarget.hidden = false;
  }

  clearError() {
    if (!this.hasErrorTarget) return;
    this.errorTarget.replaceChildren();
    this.errorTarget.hidden = true;
  }

  showInvalidTokenBlock() {
    if (this.hasFormTarget) {
      this.formTarget.hidden = true;
    }
    if (this.hasInvalidTokenBlockTarget) {
      this.invalidTokenBlockTarget.hidden = false;
      this.invalidTokenBlockTarget.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  setBusy(busy) {
    if (!this.hasSubmitTarget) return;
    this.submitTarget.disabled = busy;
    this.submitTarget.setAttribute("aria-busy", busy ? "true" : "false");
  }
}

